import React, { useState, useEffect, useRef } from "react";
import { AxiosError } from "axios";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { cloneDeep } from "lodash";
import { useTranslation } from "react-i18next";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Toast } from "primereact/toast";
import { FilterMatchMode } from "primereact/api";
import { ButtonInput } from "../../components/ButtonInput/ButtonInput";
import { ProgressSpinner } from "primereact/progressspinner";
import { DropdownInput } from "../../components/DropdownInput/DropdownInput";
import { DocumentOfReceive } from "../../models/DocumentOfReceive";
import {
  OwnershipDocument,
  CombinedDocument,
} from "../../models/OwnershipDocument";
import { Product, ProductType, ProductCategory } from "../../models/Product";
import { Partner } from "../../models/Partner";
import { Warehouse } from "../../models/Warehouse";
import { RoleType } from "../../models/Role";
import { PageLayout } from "../../layout/PageLayout/PageLayout";
import { activeUser } from "../../services/AuthService";
import { getAllWarehouses } from "../../services/WarehouseService";
import { getAllPartners } from "../../services/PartnerService";
import { getAllProducts } from "../../services/ProductService";
import { getAllProductCategories } from "../../services/ProductCategoryService";
import { getAllForeignProductDocuments } from "../../services/NewVersionService";
import { getAllOwnershipDocuments } from "../../services/NewVersionService";
import { useApplicationContext } from "../../application/ApplicationContext";
import { Utility } from "../../core/Utility";
import "./ForeignProductPage.scss";

export const ForeignProductsPage = () => {
  const { t } = useTranslation();

  const { state } = useApplicationContext();
  const businessYear = state.businessYear ?? new Date().getFullYear();

  const [selectedWarehouse, setSelectedWarehouse] = useState<Warehouse>();
  const [selectedProductCategory, setSelectedProductCategory] =
    useState<ProductCategory>();
  const [selectedProduct, setSelectedProduct] = useState<Product>(
    {} as Product
  );
  const [selectedPartner, setSelectedPartner] = useState<Partner>(
    {} as Partner
  );

  const [allMercantileInputDocuments, setAllMercantileInputDocuments] =
    useState<CombinedDocument[]>([]);
  const [mercantileInputDocuments, setMercantileInputDocuments] = useState<
    CombinedDocument[]
  >([]);
  const [nettoSum, setNettoSum] = useState<number>(0);
  const [filteredInputDocuments, setFilteredInputDocuments] = useState<
    CombinedDocument[]
  >([]);

  const [warehousesApi, setWarehousesApi] = useState<Warehouse[]>([]);
  const [partnersApi, setPartnersApi] = useState<Partner[]>([]);
  const [productsApi, setProductsApi] = useState<Product[]>([]);
  const [productCategoriesApi, setProductCategoriesApi] = useState<
    ProductCategory[]
  >([]);

  const [processing, setProcessing] = useState(false);
  const [filters, setFilters] = useState({
    finishedDocumentId: { value: null, matchMode: FilterMatchMode.CONTAINS },
    loadingCityName: { value: null, matchMode: FilterMatchMode.CONTAINS },
    netto: { value: null, matchMode: FilterMatchMode.CONTAINS },
  });

  const ensureSuccess = (resp: any) => {
    if (resp?.data?.status === "ERROR") {
      throw new Error(resp?.data?.message ?? "Greška");
    }
  };

  const refreshLookups = async () => {
    const [wResp, partnerResp, pResp, pcResp] = await Promise.all([
      getAllWarehouses(),
      getAllPartners(),
      getAllProducts(),
      getAllProductCategories(),
    ]);

    [wResp, partnerResp, pResp, pcResp].forEach(ensureSuccess);

    const wh = wResp?.data?.result ?? [];
    const pa = partnerResp?.data?.result ?? [];
    const pr = pResp?.data?.result ?? [];
    const pc = pcResp?.data?.result ?? [];

    setWarehousesApi(wh);
    setPartnersApi(pa);
    setProductsApi(pr);
    setProductCategoriesApi(pc);

    return { wh, pa, pr, pc };
  };

  useEffect(() => {
    setProcessing(true);
    refreshLookups()
      .then(() => {
        return Promise.all([
          getAllForeignProductDocuments(true),
          getAllOwnershipDocuments(),
        ]);
      })
      .then(([foreignDocsResponse, ownershipDocsResponse]) => {
        const allDocuments = foreignDocsResponse.data.result;
        const allOwnershipDocuments = ownershipDocsResponse.data.result;
        const allMercantileOwnershipDocuments = allOwnershipDocuments.filter(
          (oneOwnershipDocument: OwnershipDocument) =>
            oneOwnershipDocument.mercantile === true
        );

        // Transformišemo podatke u zajednički format
        let combinedDocuments: CombinedDocument[] = [
          ...allDocuments.map((doc: DocumentOfReceive) => ({
            id: doc.id,
            documentType: doc.input ? "PRIJEMNICA" : "OTPREMNICA",
            documentId: doc?.finishedDocumentNumber ?? doc.documentId,
            finishedDocumentId:
              doc?.finishedDocumentNumber ?? doc.finishedDocumentId,
            date: new Date(doc.date as Date),
            warehouse: { ...doc.warehouse },
            warehouseName: doc.warehouse.name,
            loadingCityName: doc.loadingCityName,
            partner: doc.input ? { ...doc.partner } : { ...doc.productOwner },
            product: { ...doc.product },
            netto: doc.netto,
            input: doc.input,
          })),
          ...allMercantileOwnershipDocuments.map((doc: OwnershipDocument) => ({
            id: doc.id,
            documentType: "VLASNICKI_LIST",
            documentId: doc?.ownerDocNumberFormatted ?? doc.ownerDocId,
            date: new Date(doc.date),
            warehouse: { ...doc.warehouse },
            warehouseName: doc.warehouse.name,
            partner: { ...doc.previousOwner },
            product: { ...doc.product },
            netto: doc.netto,
          })),
        ];
        combinedDocuments = combinedDocuments.filter((doc) => {
          const d = new Date(doc.date as Date);
          return d.getFullYear() === businessYear;
        });
        combinedDocuments.sort((a, b) => b.date.getTime() - a.date.getTime());

        setAllMercantileInputDocuments(combinedDocuments);
        setFilteredInputDocuments(combinedDocuments);
        formatMercantileDocumentsData(combinedDocuments);
      })
      .catch((error: AxiosError) => {
        console.log(error);
      })
      .finally(() => setProcessing(false));
  }, [businessYear]);

  const toast = useRef(null);
  const navigate = useNavigate();

  const resetFilters = () => {
    setFilters({
      finishedDocumentId: { value: null, matchMode: FilterMatchMode.CONTAINS },
      loadingCityName: { value: null, matchMode: FilterMatchMode.CONTAINS },
      netto: { value: null, matchMode: FilterMatchMode.CONTAINS },
    });
  };

  useEffect(() => {
    formatMercantileDocumentsData(allMercantileInputDocuments);
    resetFilters();
  }, [
    selectedWarehouse,
    selectedProduct,
    selectedProductCategory,
    selectedPartner,
  ]);

  const formatMercantileDocumentsData = (allDocuments: CombinedDocument[]) => {
    let copyOfMercantileDocuments: CombinedDocument[] = [];
    let amountOfNetto: number = 0;
    for (const mercantileInput of allDocuments) {
      //filtriranje prijemnica po magacinu, kategoriji i vrsti robe
      if (
        (selectedProductCategory?.id
          ? mercantileInput.product?.category?.id == selectedProductCategory.id
          : true) && //prijemnice za izabranu grupu robe (default za sve)...
        (selectedProduct?.id
          ? mercantileInput.product?.id == selectedProduct.id
          : true) && //prijemnice za izabranu robu (default za sve)...
        (selectedWarehouse?.id
          ? mercantileInput.warehouseName == selectedWarehouse?.name
          : true) && //...u izabranom magacinu...
        (selectedPartner?.id
          ? mercantileInput.partner?.id == selectedPartner?.id
          : true) //...za izabranog dobavljaca
      ) {
        copyOfMercantileDocuments.push(mercantileInput);
        if (mercantileInput.netto) {
          mercantileInput.input === true
            ? (amountOfNetto += mercantileInput.netto)
            : (amountOfNetto -= mercantileInput.netto);
        }
      }
    }
    copyOfMercantileDocuments.sort(
      (a: CombinedDocument, b: CombinedDocument) => {
        return (a.date as Date) < (b.date as Date) ? 1 : -1;
      }
    );
    setMercantileInputDocuments(copyOfMercantileDocuments);
    setNettoSum(amountOfNetto);
  };

  useEffect(() => {
    let amountOfNetto: number = 0;
    for (const oneInput of filteredInputDocuments) {
      if (oneInput.netto) {
        oneInput.input === true
          ? (amountOfNetto += oneInput.netto)
          : (amountOfNetto -= oneInput.netto);
      }
    }
    setNettoSum(amountOfNetto);
  }, [filteredInputDocuments]);

  const handleWarehouseChange = (prop: string, value: any) => {
    let copyOfNewWarehouse = cloneDeep(selectedWarehouse);
    copyOfNewWarehouse = warehousesApi.find(
      (item) => item.id == value
    ) as Warehouse;
    setSelectedWarehouse(copyOfNewWarehouse);
  };

  const handlePartnerChange = (prop: string, value: any) => {
    let copyOfNewPartner = cloneDeep(selectedPartner);
    copyOfNewPartner = partnersApi.find((item) => item.id == value) as Partner;
    setSelectedPartner(copyOfNewPartner);
  };

  const handleProductCategoryChange = (prop: string, value: any) => {
    const copyOfNewProductCategory = productCategoriesApi.find(
      (item) => item.id == value
    ) as ProductCategory;
    setSelectedProductCategory(copyOfNewProductCategory);
    setSelectedProduct({} as Product);
  };

  const handleProductChange = (prop: string, value: any) => {
    let copyOfNewProduct = cloneDeep(selectedProduct);
    copyOfNewProduct = productsApi.find((item) => item.id == value) as Product;
    setSelectedProduct(copyOfNewProduct);
  };

  const {
    control,
    formState: { errors },
    reset,
    setValue,
  } = useForm({
    mode: "all",
  });

  const formatFinishedDocumentId = (rowData: CombinedDocument) => {
    return rowData?.finishedDocumentId ?? rowData?.documentId ?? "/";
  };

  const formatDocumentType = (rowData: CombinedDocument) => {
    switch (rowData.documentType) {
      case "PRIJEMNICA":
        return "Prijemnica";
      case "OTPREMNICA":
        return "Otpremnica";
      case "VLASNICKI_LIST":
        return "Vlasnički list";
      default:
        return "/";
    }
  };

  const formatProductName = (rowData: CombinedDocument) => {
    return productsApi?.find((oneProduct) => oneProduct.id == rowData.product?.id)
      ?.name;
  };

  const formatPartnerName = (rowData: CombinedDocument) => {
    return rowData.partner?.name;
  };

  const formatDate = (rowData: CombinedDocument) => {
    return Utility.formatDate(new Date(rowData.date as Date));
  };

  const amountNetto = (rowData: CombinedDocument) => {
    return rowData?.netto ? Utility.addThousandSeparator(rowData.netto) : "";
  };

  const formatOptions = (rowData: CombinedDocument) => {
    return (
      <div className="action-buttons">
        <ButtonInput
          addStyle={{ border: "none", background: "#198754" }}
          labelText={""}
          icon="bi bi-eye"
          onButtonClick={() => {
            const id = rowData.id;
            if (rowData.documentType === "PRIJEMNICA") {
              navigate(`/document-of-receive-preview-fast/${id}`);
            } else if (rowData.documentType === "OTPREMNICA") {
              navigate(`/document-of-output-preview-fast/${id}`);
            } else {
              navigate(`/ownership-document/${id}`);
            }
          }}
        />
      </div>
    );
  };

  const exportToExcel = () => {
    const excelMercantileObject = filteredInputDocuments.map(
      (item: CombinedDocument) => {
        return {
          BrojDokumenta: item?.documentId ?? "/",
          Tip: item.documentType,
          Datum: Utility.formatDate(new Date(item.date as Date)),
          Magacin: item.warehouseName,
          Dobavljac: item.partner?.name,
          Roba: item.product?.name,
          Neto: item.netto,
        };
      }
    );
    const excelObject = [
      {
        BrojDokumenta: "",
        Tip: "",
        Datum: "",
        Magacin: "",
        Dobavljac: "",
        Roba: "",
        Neto: "",
      },
      ...excelMercantileObject,
    ];
    import("xlsx").then((xlsx) => {
      const worksheet = xlsx.utils.json_to_sheet(excelObject);
      const workbook = { Sheets: { data: worksheet }, SheetNames: ["data"] };
      const excelBuffer = xlsx.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });
      saveAsExcelFile(
        excelBuffer,
        `Tudja merkantilna roba na lageru ${Utility.formatDate(new Date())}`
      );
    });
  };

  const saveAsExcelFile = (buffer: any, fileName: any) => {
    import("file-saver").then((module: any) => {
      if (module && module.default) {
        let EXCEL_TYPE =
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
        let EXCEL_EXTENSION = ".xlsx";
        const data = new Blob([buffer], {
          type: EXCEL_TYPE,
        });
        module.default.saveAs(data, fileName + EXCEL_EXTENSION);
      }
    });
  };

  return (
    <PageLayout>
      <Toast ref={toast} position={"top-right"} />
      <div className="table-wrapper">
        <div className="table-header">
          <div className="header-left">
            <i
              className="bi bi-boxes header-title-icon"
              style={{
                fontSize: 14,
                marginLeft: "1rem",
              }}
            ></i>
            <span className="header-left-title">
              {t("Tudja merkantilna roba na lageru")}
            </span>
            <span className="header-elements-text">{`${t("u")}`}</span>
            <DropdownInput
              id="warehouse"
              value={selectedWarehouse?.id}
              onValueChange={handleWarehouseChange}
              optionValue="id"
              optionLabel="name"
              options={warehousesApi.filter((item) => item.virtual !== true)}
              labelText=""
              placeholderText={t("Magacin")}
              control={control}
              setValue={setValue}
              errors={errors}
              disabled={
                activeUser?.role?.name === RoleType.Warehouseman || processing
              }
            />
            <span className="header-elements-text">{`${t("za")}`}</span>
            <DropdownInput
              id="productCategory"
              value={selectedProductCategory?.id}
              onValueChange={handleProductCategoryChange}
              optionValue="id"
              optionLabel="name"
              options={productCategoriesApi.filter(
                (item) => item.type == ProductType.MercantileGoods
              )}
              filter={false}
              labelText=""
              placeholderText={t("vrstu robe")}
              control={control}
              setValue={setValue}
              errors={errors}
              rules={{ required: true }}
              disabled={processing}
            />
            <span className="header-elements"> </span>
            <DropdownInput
              id="selectedProduct"
              value={selectedProduct?.id}
              onValueChange={handleProductChange}
              optionValue="id"
              optionLabel="name"
              options={productsApi.filter(
                (item) => item.category?.id === selectedProductCategory?.id
              )}
              filter={false}
              labelText=""
              placeholderText={t("Ime")}
              control={control}
              setValue={setValue}
              errors={errors}
              rules={{ required: true }}
              disabled={selectedProductCategory === undefined}
            />
            <span className="header-elements">vlasnik</span>
            <DropdownInput
              id="selectedPartner"
              value={selectedPartner?.id}
              onValueChange={handlePartnerChange}
              optionValue="id"
              optionLabel="name"
              options={partnersApi}
              filter={true}
              labelText=""
              placeholderText={t("Vlasnik")}
              control={control}
              setValue={setValue}
              errors={errors}
              rules={{ required: true }}
            />
            <ButtonInput
              addStyle={{
                border: "none",
                background: "#ffffff",
                color: "#6C757D",
                margin: "0 0 0 1rem",
              }}
              labelText={"reset"}
              icon="bi bi-arrow-repeat"
              onButtonClick={() => {
                setSelectedWarehouse({} as Warehouse);
                setSelectedProduct({} as Product);
                setSelectedProductCategory({} as ProductCategory);
                setSelectedPartner({} as Partner);
              }}
              disabled={processing}
            />
          </div>
          <div className="header-right">
            <ButtonInput
              addStyle={{
                border: "1px solid #01a453",
                background: "#ffffff",
                color: "#01a453",
                marginLeft: "5px",
              }}
              labelText={""}
              icon="bi bi-file-earmark-excel"
              onButtonClick={exportToExcel}
              disabled={processing === true}
            />
          </div>
        </div>
        {processing ? (
          <ProgressSpinner />
        ) : (
          <div className="table-body">
            <DataTable
              scrollable={true}
              scrollHeight="80vh"
              sortMode="single"
              removableSort
              filters={filters}
              filterDisplay="row"
              emptyMessage="Nema tudje robe na lageru za zadate kriterijume"
              value={mercantileInputDocuments}
              onValueChange={(filteredData) =>
                setFilteredInputDocuments(filteredData)
              }
              showGridlines
              stripedRows
            >
              <Column
                frozen
                body={formatFinishedDocumentId}
                filterField={"finishedDocumentId"}
                filter
                header={t("Br.Dok.")}
                style={{ minWidth: "100px" }}
                footer=""
                footerStyle={{
                  backgroundColor: "#6C757D",
                  color: "#FFFFFF",
                  border: "none",
                }}
              />
              <Column
                body={formatDocumentType}
                header={t("Tip")}
                style={{ minWidth: "100px" }}
                footer=""
                footerStyle={{
                  backgroundColor: "#6C757D",
                  color: "#FFFFFF",
                  border: "none",
                }}
              />
              <Column
                body={formatDate}
                header={t("Datum")}
                style={{ minWidth: "80px" }}
                footer=""
                footerStyle={{
                  backgroundColor: "#6C757D",
                  color: "#FFFFFF",
                  border: "none",
                }}
              />
              <Column
                field="warehouseName"
                header={t("Magacin")}
                style={{ minWidth: "90px" }}
                footer=""
                footerStyle={{
                  backgroundColor: "#6C757D",
                  color: "#FFFFFF",
                  border: "none",
                }}
              />
              <Column
                body={formatPartnerName}
                header={t("Vlasnik")}
                style={{ minWidth: "100px" }}
                footer=""
                footerStyle={{
                  backgroundColor: "#6C757D",
                  color: "#FFFFFF",
                  border: "none",
                }}
              />
              <Column
                body={formatProductName}
                header={t("Naziv robe")}
                style={{ minWidth: "100px" }}
                footer=""
                footerStyle={{
                  backgroundColor: "#6C757D",
                  color: "#FFFFFF",
                  border: "none",
                }}
              />
              <Column
                body={amountNetto}
                filterField={"netto"}
                style={{ minWidth: "80px" }}
                filter
                header={t("Neto")}
                footer={
                  "Stanje: " + Utility.addThousandSeparator(nettoSum) + " kg"
                }
                footerStyle={{
                  backgroundColor: "#6C757D",
                  color: "#FFFFFF",
                  border: "none",
                }}
              />
              <Column
                body={formatOptions}
                header={t("Opcije")}
                style={{ minWidth: "150px" }}
                footer=""
                footerStyle={{
                  backgroundColor: "#6C757D",
                  color: "#FFFFFF",
                  border: "none",
                }}
              />
            </DataTable>
          </div>
        )}
      </div>
    </PageLayout>
  );
};
